import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SafariExperiencesCarousel from '../../components/SafariExperiencesCarousel'
import FAQSection from '../../components/FAQSection'
import { safariExperiences } from '../../data/safariExperiences'

export default function GorillasLions5Days() {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const faqs = [
        {
            question: "Is 5 days enough for a meaningful safari?",
            answer: "Absolutely! This itinerary focuses on quality over quantity. You'll experience Uganda's two most iconic sightings—mountain gorillas and tree-climbing lions—plus excellent game viewing and boat safaris. The concentrated timeframe means every day delivers memorable wildlife encounters without exhausting travel schedules."
        },
        {
            question: "How likely am I to see the tree-climbing lions?",
            answer: "Success rates are approximately 70-80%. The lions don't climb trees constantly—they tend to do so during hot midday hours to escape heat and tsetse flies. Your guide knows the best spots and timing. Even if lions aren't in trees during your visit, you'll likely spot them on the ground, plus plenty of other wildlife in Ishasha."
        },
        {
            question: "Can this safari be combined with other destinations?",
            answer: "Definitely! This 5-day safari works perfectly as an Uganda extension to Kenya or Tanzania trips, or before/after Zanzibar beach time. It's also ideal for business travelers in Kampala wanting to add safari experiences. We can arrange seamless connections to other East African destinations."
        },
        {
            question: "What fitness level do I need?",
            answer: "Moderate fitness is recommended primarily for the gorilla trek, which can be challenging (2-8 hours over steep terrain). However, rangers assign you to gorilla families based on your fitness level expressed during the briefing. The rest of the safari involves comfortable game drives and boat cruises requiring minimal physical exertion."
        }
    ]

    const itinerary = [
        {
            day: 1,
            title: "Arrival & Transfer to Queen Elizabeth National Park",
            description: "Upon arrival at Entebbe International Airport or pick-up from your Kampala hotel, you will meet your safari guide and we'll embark on a scenic journey to Queen Elizabeth National Park. We'll travel for approximately 6-7 hours through Uganda's beautiful countryside, with a stop at the Equator monument where you will have the opportunity for photos and watch a fascinating water experiment demonstrating hemispheric differences. You will arrive at your lodge in time for an optional evening game drive or simply relax and prepare for your adventure ahead.",
            highlights: ["Airport/hotel pickup", "Equator monument stop", "Scenic drive", "Optional evening game drive"]
        },
        {
            day: 2,
            title: "Queen Elizabeth - Game Drive & Kazinga Channel Boat Safari",
            description: "We'll start your day with an early morning game drive across the Kasenyi plains, prime territory for lion sightings along with elephants, buffaloes, Uganda kobs, and various antelope species. The morning light offers excellent photography opportunities. You will return to your lodge for lunch. In the afternoon, we'll embark on a thrilling boat cruise along the Kazinga Channel, famous for having the highest concentration of hippos in Africa. You will watch crocodiles basking on the banks and spot incredible birdlife including African fish eagles, kingfishers, and cormorants.",
            highlights: ["Morning game drive on Kasenyi plains", "Kazinga Channel boat cruise", "Hippo pods and crocodiles", "Spectacular birdwatching"]
        },
        {
            day: 3,
            title: "Ishasha Tree-Climbing Lions & Transfer to Bwindi",
            description: "After breakfast, we'll head to the southern Ishasha sector of Queen Elizabeth National Park, world-famous for its unique tree-climbing lions. You will watch for these majestic predators lounging in ancient fig trees—a behavior found in only two places on Earth. The sight of a lion perched in branches is unforgettable! We'll continue your journey to Bwindi Impenetrable National Park, passing through stunning highland scenery with terraced hillsides and traditional villages. You will arrive at your lodge near Bwindi where you will enjoy dinner and rest before tomorrow's gorilla trek.",
            highlights: ["Tree-climbing lions in Ishasha", "Scenic highland drive", "Terraced hillsides", "Arrive at Bwindi"]
        },
        {
            day: 4,
            title: "Gorilla Trekking in Bwindi Impenetrable Forest",
            description: "This is the day you've been waiting for! After an early breakfast, we'll head to the park headquarters for a comprehensive briefing on gorilla trekking protocols. Then, you will trek into Bwindi's ancient rainforest with experienced rangers to find a habituated gorilla family. The trek can last anywhere from 2-8 hours depending on where the gorillas spent the night. Once you find them, you will spend one magical hour observing these gentle giants as they feed, play, and interact—watching a silverback protect his family or youngsters tumbling together is truly life-changing. In the afternoon, if you wish, we can arrange an optional visit to the Batwa community where you will learn about their traditional forest lifestyle and ancient survival skills.",
            highlights: ["Gorilla trekking in ancient rainforest", "1 hour with mountain gorillas", "Expert ranger guides", "Optional Batwa cultural visit"]
        },
        {
            day: 5,
            title: "Return to Entebbe/Kampala & Departure",
            description: "After a leisurely breakfast, we'll begin your scenic journey back to Entebbe or Kampala. The drive allows time for you to reflect on your incredible wildlife encounters—from lions in trees to mountain gorillas in misty forests. We'll stop again at the Equator for souvenir shopping if you didn't on Day 1. You will arrive in Entebbe in time for your departure flight or overnight stay, depending on your schedule. We'll ensure you reach the airport with plenty of time before your flight.",
            highlights: ["Scenic return drive", "Equator souvenir shopping", "Airport transfer", "Departure"]
        }
    ]

    return (
        <div>
            {/* Hero Section */}
            <section
                className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
                style={{
                    backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(/images/packages/5-days-gorillas-lions.jpg)',
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl text-white">
                        <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
                            5 Day Gorillas and Lions Tour – 2025/2026
                        </h1>
                        <p className="text-xl md:text-2xl mb-6">
                            Experience Uganda's most iconic wildlife in just five days
                        </p>
                        <div className="flex items-center gap-6 mb-6">
                            <div className="flex items-center gap-2">
                                <div className="flex text-safari-gold">
                                    {[...Array(5)].map((_, i) => (
                                        <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                                    ))}
                                </div>
                                <span className="text-lg">(185+ reviews)</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mb-8">
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                                UWA Certified
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />
                                Perfect for Limited Time
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                                Eco-Tourism
                            </div>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            Request Your Safari
                        </button>
                    </div>
                </div>
            </section>

            {/* Page Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="lg:grid lg:grid-cols-3 lg:gap-12">
                    {/* Main Content */}
                    <main className="lg:col-span-2">
                        {/* Safari Description */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                                Maximum Impact in Minimum Time
                            </h2>

                            <div className="prose prose-lg max-w-none">
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    Short on time but not willing to compromise on experiences? This 5-day safari delivers Uganda's two most iconic wildlife encounters: mountain gorillas in Bwindi's misty rainforests and the rare tree-climbing lions of Ishasha. It's the perfect introduction to Uganda for travelers with limited vacation days but unlimited sense of adventure.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    You'll spend quality time in Queen Elizabeth National Park, Uganda's most biodiverse protected area, with morning game drives across the Kasenyi plains and an afternoon boat cruise on the Kazinga Channel, where Africa's highest concentration of hippos wallow and grunt in the water. Then, journey south through stunning highland scenery to Bwindi Impenetrable National Park for the experience of a lifetime—one hour with endangered mountain gorillas.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Why This Safari Works So Well</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    This itinerary is brilliantly efficient. By focusing on two key destinations—Queen Elizabeth and Bwindi—you avoid excessive driving while still experiencing incredible wildlife diversity. The tree-climbing lions of Ishasha are found nowhere else in Uganda (and only in one other park in the world), making this a truly unique sighting. Combine that with gorilla trekking, and you've got Uganda's greatest hits in a manageable timeframe.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    The relatively short duration also makes this safari more budget-friendly while still delivering transformative experiences. You're not sacrificing quality for speed—each day is carefully designed to maximize wildlife encounters. The pacing allows you to truly absorb each moment rather than rushing from park to park.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Perfect For</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    This safari suits travelers with limited time who want gorilla trekking as their priority, complemented by classic safari experiences. It's ideal for combining with beach time in Zanzibar, business trips to Kampala, or as a Uganda extension to Kenya/Tanzania safaris. First-time Africa visitors get a concentrated introduction to East African wildlife without the overwhelm of longer itineraries.
                                </p>
                            </div>
                        </section>

                        {/* Safari Highlights */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                                Safari Highlights
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-paw" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Mountain Gorilla Encounter</h3>
                                        <p className="text-gray-700">One unforgettable hour with endangered mountain gorillas in Bwindi</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-lion" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Tree-Climbing Lions</h3>
                                        <p className="text-gray-700">Witness the rare sight of lions lounging in Ishasha's ancient fig trees</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-ship" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Kazinga Channel Cruise</h3>
                                        <p className="text-gray-700">Boat past hundreds of hippos and Nile crocodiles in their natural habitat</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-binoculars" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Kasenyi Plains Game Drive</h3>
                                        <p className="text-gray-700">Search for elephants, buffaloes, and predators in open savannah</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Cultural Encounters</h3>
                                        <p className="text-gray-700">Optional Batwa community visit to learn ancient forest survival skills</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-mountain" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Highland Scenery</h3>
                                        <p className="text-gray-700">Drive through breathtaking terraced hillsides and traditional villages</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Day-by-Day Itinerary */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                                Your 5-Day Itinerary
                            </h2>
                            <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                                <p className="text-gray-700 text-lg">
                                    <strong>📋 Tentative Itinerary:</strong> This itinerary is flexible and can be adjusted based on your travel preferences, budget, and specific interests. We customize every safari to match your unique needs!
                                </p>
                            </div>

                            <div className="space-y-6">
                                {itinerary.map((day) => (
                                    <div key={day.day} className="card">
                                        <div className="p-6">
                                            <div className="flex gap-4">
                                                <div className="flex-shrink-0">
                                                    <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                                                        {day.day}
                                                    </div>
                                                </div>
                                                <div className="flex-grow">
                                                    <h3 className="text-xl font-bold text-safari-green mb-2">{day.title}</h3>
                                                    <p className="text-gray-700 mb-4">{day.description}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {day.highlights.map((highlight, index) => (
                                                            <span
                                                                key={index}
                                                                className="bg-safari-light text-safari-green px-3 py-1 rounded-full text-sm"
                                                            >
                                                                {highlight}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* What's Included/Excluded */}
                        <section className="mb-16">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                                        What's Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">4 nights accommodation</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">4x4 safari vehicle with expert guide</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Gorilla trekking permit</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">All park entry fees</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Game drives and boat cruise</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Full board meals</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Bottled drinking water</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                                        What's Not Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">International flights</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Uganda visa ($50)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Travel insurance</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Tips for guides and staff</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Personal expenses</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Alcoholic beverages</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Porter hire (recommended)</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* FAQs */}
                        <FAQSection faqs={faqs} title="Common Questions" />

                        {/* Related Safari Experiences Carousel */}
                        <section>
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-4 text-center">
                                Explore More Safari Experiences
                            </h2>
                            <p className="text-center text-gray-700 mb-12 text-lg">
                                Discover other carefully curated safari adventures across Uganda
                            </p>

                            <SafariExperiencesCarousel
                                experiences={safariExperiences}
                                currentExperience="5-days-gorillas-lions"
                            />
                        </section>
                    </main>

                    {/* Sidebar */}
                    <aside className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="card p-6 mb-8">
                                <h3 className="text-2xl font-bold text-safari-green mb-4">Safari Details</h3>
                                <div className="space-y-4 text-gray-700">
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Duration:</strong> 5 Days / 4 Nights</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Best Time:</strong> Year-round</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-map-marked-alt" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Parks:</strong> 2 National Parks</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Difficulty:</strong> Moderate</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Ideal For:</strong> Limited time travelers</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                                >
                                    Get Custom Quote
                                </button>

                                <div className="bg-safari-green/5 border border-safari-green/20 rounded-lg p-4 mt-4">
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        <span className="font-bold text-safari-green">🌿 Travel with Purpose:</span> Part of your booking contributes to our <Link to="/planting-green-paths" className="text-safari-green font-semibold hover:underline">'Planting Green Paths'</Link> tree planting initiative, helping restore Uganda's ecosystems by securing a tree, planting and tree growth follow up. You can choose to participate in planting a tree activity during your safari or simply support the cause.
                                    </p>
                                </div>
                            </div>

                            <div className="card p-6 mb-8">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6 text-safari-gold" />
                                    Packing Essentials
                                </h3>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Waterproof hiking boots</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Long pants & sleeves</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Rain jacket</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Gardening gloves</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Binoculars & camera</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Sunscreen & insect repellent</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="card p-6">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-info-circle" className="w-6 h-6" />
                                    Good to Know
                                </h3>
                                <ul className="space-y-3 text-gray-700 text-sm">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Minimum age: 15 years for gorilla trekking</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Porter hire strongly recommended</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Book gorilla permits 3-6 months ahead</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <BookingModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                experience="5 Day Gorillas and Lions Tour"
            />
        </div>
    )
}
